/**
 * MELANUA Cart Drawer
 * Slide-out cart panel with luxury styling
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePreview } from '../context/PreviewContext';

function CartItem({ item, onUpdateQuantity, onRemove }) {
    const { product, variant, quantity, unit_price, line_total } = item;

    return (
        <div className="flex gap-4 py-4 border-b border-champagne/20">
            {/* Product Image */}
            <div className="w-20 h-20 bg-champagne/10 rounded-xl overflow-hidden flex-shrink-0">
                <img
                    src={product.image_url || '/images/placeholder.jpg'}
                    alt={product.name}
                    className="w-full h-full object-contain"
                />
            </div>

            {/* Product Details */}
            <div className="flex-1 min-w-0">
                <h4 className="font-serif text-charcoal font-medium truncate">
                    {product.name}
                </h4>
                {variant && (
                    <p className="text-sm text-charcoal/60">{variant.name}</p>
                )}
                <p className="text-charcoal font-bold mt-1">
                    ${parseFloat(unit_price).toFixed(2)}
                </p>

                {/* Quantity Controls */}
                <div className="flex items-center gap-2 mt-2">
                    <button
                        onClick={() => onUpdateQuantity(item.id, Math.max(1, quantity - 1))}
                        className="w-7 h-7 rounded-full border border-champagne/30 flex items-center justify-center text-charcoal/60 hover:border-champagne-gold hover:text-champagne-gold transition-colors"
                        disabled={quantity <= 1}
                    >
                        -
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{quantity}</span>
                    <button
                        onClick={() => onUpdateQuantity(item.id, quantity + 1)}
                        className="w-7 h-7 rounded-full border border-champagne/30 flex items-center justify-center text-charcoal/60 hover:border-champagne-gold hover:text-champagne-gold transition-colors"
                    >
                        +
                    </button>

                    <button
                        onClick={() => onRemove(item.id)}
                        className="ml-auto text-charcoal/40 hover:text-red-500 transition-colors"
                        title="Remove item"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Line Total */}
            <div className="text-right">
                <p className="font-bold text-charcoal">
                    ${parseFloat(line_total).toFixed(2)}
                </p>
            </div>
        </div>
    );
}

export default function CartDrawer() {
    const { cart, isCartOpen, closeCart, updateItem, removeItem, isLoading } = useCart();
    const { isPreviewMode } = usePreview();

    if (!isCartOpen) return null;

    // If not in preview mode, don't show cart drawer with prices
    if (!isPreviewMode) return null;

    const handleUpdateQuantity = async (itemId, quantity) => {
        await updateItem(itemId, quantity);
    };

    const handleRemove = async (itemId) => {
        await removeItem(itemId);
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 z-[60] transition-opacity"
                onClick={closeCart}
            />

            {/* Drawer */}
            <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[60] shadow-2xl flex flex-col transform transition-transform">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-champagne/20">
                    <h2 className="font-serif text-2xl text-charcoal">Your Cart</h2>
                    <button
                        onClick={closeCart}
                        className="w-10 h-10 rounded-full hover:bg-champagne/20 flex items-center justify-center transition-colors"
                    >
                        <svg className="w-6 h-6 text-charcoal" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto px-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-2 border-champagne-gold border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : !cart.items || cart.items.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-16 h-16 bg-champagne/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-champagne-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                            </div>
                            <h3 className="font-serif text-xl text-charcoal mb-2">Your cart is empty</h3>
                            <p className="text-charcoal/60 mb-6">
                                Discover our luxury skincare collection
                            </p>
                            <Link
                                to="/products/"
                                onClick={closeCart}
                                className="btn-primary inline-flex"
                            >
                                Shop Now
                            </Link>
                        </div>
                    ) : (
                        <div className="py-2">
                            {cart.items.map((item) => (
                                <CartItem
                                    key={item.id}
                                    item={item}
                                    onUpdateQuantity={handleUpdateQuantity}
                                    onRemove={handleRemove}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {cart.items?.length > 0 && (
                    <div className="border-t border-champagne/20 px-6 py-4 bg-soft-beige/30">
                        {/* Subtotal */}
                        <div className="flex justify-between items-center mb-4">
                            <span className="text-charcoal/70">Subtotal</span>
                            <span className="text-xl text-charcoal font-bold">
                                ${parseFloat(cart.subtotal).toFixed(2)}
                            </span>
                        </div>

                        <p className="text-sm text-charcoal/60 mb-4 text-center">
                            Shipping & taxes calculated at checkout
                        </p>

                        {/* Checkout Button */}
                        <Link
                            to="/checkout/"
                            onClick={closeCart}
                            className="btn-primary w-full justify-center text-center py-4"
                        >
                            Proceed to Checkout
                        </Link>

                        {/* Continue Shopping */}
                        <button
                            onClick={closeCart}
                            className="w-full mt-3 text-center text-charcoal/70 hover:text-champagne-gold transition-colors text-sm"
                        >
                            Continue Shopping
                        </button>
                    </div>
                )}
            </div>
        </>
    );
}
